import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Plus } from "lucide-react";

interface Template {
  id: string;
  name: string;
  content: string;
}

interface RealEstateProfile {
  name: string;
  adminName: string;
}

const defaultTemplates: Template[] = [
  {
    id: "1",
    name: "Verificação de Disponibilidade",
    content: "Olá {proprietario}, sou {admin} da {imobiliaria}, gostaria de confirmar se o imóvel {imovel} ainda está disponível.\n\nLink: {link}\nValor: {valor}",
  },
  {
    id: "2",
    name: "Atualização de Preço",
    content: "Olá {proprietario}, sou {admin} da {imobiliaria}, gostaria de verificar se houve alguma atualização no valor do imóvel {imovel}.\n\nValor atual: {valor}\nLink: {link}",
  },
  {
    id: "3",
    name: "Agendamento de Visita",
    content: "Olá {proprietario}, sou {admin} da {imobiliaria}, tenho interesse no imóvel {imovel} e gostaria de agendar uma visita.\n\nDetalhes do imóvel:\nLink: {link}\nValor: {valor}",
  },
];

interface MessageTemplatesProps {
  onSelectTemplate: (content: string) => void;
}

export const MessageTemplates = ({ onSelectTemplate }: MessageTemplatesProps) => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Template[]>(defaultTemplates);
  const [newTemplate, setNewTemplate] = useState({ name: "", content: "" });
  const [showNewTemplateDialog, setShowNewTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [profile, setProfile] = useState<RealEstateProfile | null>(null);

  useEffect(() => {
    const storedProfile = localStorage.getItem('realEstateProfile');
    if (storedProfile) {
      setProfile(JSON.parse(storedProfile));
    }
  }, []);

  const handleSaveTemplate = () => {
    if (!newTemplate.name || !newTemplate.content) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos do template.",
        variant: "destructive",
      });
      return;
    }

    if (editingTemplate) {
      setTemplates(templates.map(t => 
        t.id === editingTemplate.id 
          ? { ...t, name: newTemplate.name, content: newTemplate.content }
          : t
      ));
      setEditingTemplate(null);
      toast({
        title: "Template atualizado",
        description: "O template foi atualizado com sucesso.",
      });
    } else {
      const template: Template = {
        id: Date.now().toString(),
        name: newTemplate.name,
        content: newTemplate.content,
      };
      setTemplates([...templates, template]);
      toast({
        title: "Template salvo",
        description: "O novo template foi salvo com sucesso.",
      });
    }

    setNewTemplate({ name: "", content: "" });
    setShowNewTemplateDialog(false);
  };

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setNewTemplate({ name: template.name, content: template.content });
    setShowNewTemplateDialog(true);
  };

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    let content = template.content;
    
    if (profile) {
      content = content
        .replace("{imobiliaria}", profile.name)
        .replace("{admin}", profile.adminName);
    }
    
    onSelectTemplate(content);
    toast({
      title: "Template selecionado",
      description: `Template "${template.name}" foi aplicado à mensagem.`,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Templates de Mensagem</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              Opções <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <Dialog open={showNewTemplateDialog} onOpenChange={setShowNewTemplateDialog}>
              <DialogTrigger asChild>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <Plus className="mr-2 h-4 w-4" /> Novo Template
                </DropdownMenuItem>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingTemplate ? "Editar Template" : "Criar Novo Template"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingTemplate 
                      ? "Edite o template de mensagem existente."
                      : "Crie um novo template de mensagem com variáveis personalizadas."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Nome do Template</label>
                    <Input
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      placeholder="Ex: Verificação de Disponibilidade"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Conteúdo</label>
                    <div className="text-xs text-gray-500 mb-2">
                      Variáveis disponíveis: {"{proprietario}"}, {"{imovel}"}, {"{link}"}, {"{valor}"}, {"{admin}"}, {"{imobiliaria}"}
                    </div>
                    <Textarea
                      value={newTemplate.content}
                      onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                      placeholder="Digite o conteúdo do template..."
                      rows={6}
                    />
                  </div>
                  <Button onClick={handleSaveTemplate} className="w-full">
                    {editingTemplate ? "Atualizar Template" : "Salvar Template"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ScrollArea className="h-[200px] rounded-md border p-2">
        <div className="space-y-2">
          {templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center justify-between p-2 hover:bg-accent rounded-lg cursor-pointer"
              onClick={() => handleSelectTemplate(template)}
            >
              <span className="text-sm font-medium">{template.name}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectTemplate(template);
                }}
              >
                Usar Template
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};